import React, { useState, useEffect } from 'react';
import { Menu, MenuProps } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { useModel, history } from '@umijs/max';
import type { MenuInfo } from 'rc-menu/lib/interface';
import './index.less';

interface MenuItem {
  id: string;
  key: string;
  label: string;
  menuCode: string;
  menuName: string;
  menuLevel: string;
  pid: string;
  children?: MenuItem[];
  icon?: React.ReactNode;
}

const HeaderMenu: React.FC = () => {
  const { userInfo, permissions, menus } = useModel('user');
  const [currentMenu, setCurrentMenu] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(0); // 默认仪表板

  // 页面代码映射 - 与Vue版本保持一致
  const MENU_PAGE: Record<number, string> = {
    99: 'DASHBOARD',
    10: 'GIS',
    25: 'SPACE_VIEW',
    2: 'WORK_CALENDAR',
    51: 'KEY_EQUIPMENT',
    3: 'STAFF_SCHEDULE',
    5: 'INSPECTION_RULE',
    50: 'DEFECT_CHECK',
    20: 'PROJECT_CONFIRM',
    7: 'SYSTEM_MANAGE',
    11: 'COMMAND_WO',
    1: 'DEFECT_WO',
    6: 'ACCOUNT',
    16: 'PERMISSION',
    30: 'JOB_PLAN_SETTING',
    31: 'JOB_SOP_SETTING',
    32: 'PHOTO_TEMPLATE_SETTING',
    98: 'DEFECT_SANKEY',
    61: 'SHIFT_MANAGE',
    62: 'SCHEDULE_TEMPLATE',
    71: 'FACILITY_TYPE_MANAGE',
  };

  // 页面路由映射
  const PAGE_ROUTES: Record<string, string> = {
    DASHBOARD: '/dashboardWorkOrder',
    GIS: '/home',
    SPACE_VIEW: '/project-view',
    WORK_CALENDAR: '/work-calendar',
    KEY_EQUIPMENT: '/equipment-manage-new',
    STAFF_SCHEDULE: '/employee-schedule', ///staff-schedule
    INSPECTION_RULE: '/quality-inspection',
    DEFECT_CHECK: '/defect-check',
    PROJECT_CONFIRM: '/project-confirm',
    SYSTEM_MANAGE: '/project-manage',
    COMMAND_WO: '/command-wo',
    DEFECT_WO: '/defect-wo',
    ACCOUNT: '/account',
    PERMISSION: '/department-station',
    JOB_PLAN_SETTING: '/work-plan',
    JOB_SOP_SETTING: '/sop-config',
    PHOTO_TEMPLATE_SETTING: '/photo-template',
    DEFECT_SANKEY: '/defect-sankey',
    // BASE_DEFECT_DISPLAY: '/defect-display', // 缺陷展示大屏
    SHIFT_MANAGE: '/shift_manage',
    SCHEDULE_TEMPLATE: '/schedule_template',
    FACILITY_TYPE_MANAGE: '/facility-type-manage',
  };

  // 获取菜单标题
  const getMenuTitle = (menu: any): string => {
    const code = MENU_PAGE[currentPage];
    if (code && menus?.length) {
      const select = menus.find(
        (el: any) => el.pid === menu.id && el.menuCode === code,
      );
      if (select) {
        return select.menuName;
      }
    }
    return menu.menuName;
  };

  // 构建权限菜单
  const buildPermissionMenus = (): MenuItem[] => {
    if (!menus?.length) return [];

    const menuItems: MenuItem[] = [];
    const menuMap: Record<string, MenuItem> = {};

    // 先处理一级菜单
    menus.forEach((el: any) => {
      if (el.menuLevel === '1') {
        const title = getMenuTitle(el);
        const menu: MenuItem = {
          id: el.id,
          key: el.id,
          label: title,
          menuCode: el.menuCode,
          menuName: el.menuName,
          menuLevel: el.menuLevel,
          pid: el.pid,
        };
        menuItems.push(menu);
        menuMap[el.id] = menu;
      }
    });

    // 再处理二级菜单
    menus.forEach((el: any) => {
      if (el.menuLevel === '2') {
        const parent = menuMap[el.pid];
        if (parent) {
          if (!parent.children) parent.children = [];
          parent.children.push({
            id: el.id,
            key: el.id,
            label: el.menuName,
            menuCode: el.menuCode,
            menuName: el.menuName,
            menuLevel: el.menuLevel,
            pid: el.pid,
          });
        }
      }
    });

    // 为有子菜单的项目添加下拉图标
    menuItems.forEach((el: MenuItem) => {
      if (el.children && el.children.length > 0) {
        el.icon = <DownOutlined />;
      }
    });

    return menuItems;
  };

  // 菜单点击处理
  const handleMenuSelect = (info: MenuInfo) => {
    const selectedItem = findMenuItemByKey(buildPermissionMenus(), info.key as string);
    if (!selectedItem) return;

    console.log('handleMenuSelect', selectedItem);

    // 根据menuCode处理跳转
    const route = PAGE_ROUTES[selectedItem.menuCode];
    if (route) {
      // 更新当前页面状态
      const pageCode = Object.keys(MENU_PAGE).find(
        (key) => MENU_PAGE[Number(key)] === selectedItem.menuCode,
      );
      if (pageCode) {
        setCurrentPage(Number(pageCode));
        // 可以在这里保存到用户状态中
      }

      // 跳转页面
      history.replace(route);
    }
  };

  // 递归查找菜单项
  const findMenuItemByKey = (items: MenuItem[], key: string): MenuItem | null => {
    for (const item of items) {
      if (item.key === key) return item;
      if (item.children) {
        const found = findMenuItemByKey(item.children, key);
        if (found) return found;
      }
    }
    return null;
  };

  // 根据当前路由初始化 currentPage
  useEffect(() => {
    const currentPath = history.location.pathname;
    console.log('当前路由:', currentPath);

    // 根据路由找到对应的页面代码
    for (const [pageCode, route] of Object.entries(PAGE_ROUTES)) {
      if (currentPath === route || currentPath.startsWith(route + '/')) {
        // 根据页面代码找到对应的页面ID
        for (const [pageId, code] of Object.entries(MENU_PAGE)) {
          if (code === pageCode) {
            console.log('设置当前页面:', pageId, pageCode, route);
            setCurrentPage(Number(pageId));
            return;
          }
        }
      }
    }

    // 如果没有找到匹配的路由，保持默认值（仪表板）
    console.log('未找到匹配的路由，使用默认页面');
  }, []); // 只在组件挂载时执行一次

  // 根据当前页面更新选中的菜单
  useEffect(() => {
    const code = MENU_PAGE[currentPage];
    if (code && menus?.length) {
      const selectedMenus = menus
        .filter((el: any) => el.menuCode === code)
        .map((el: any) => el.id);
      setCurrentMenu(selectedMenus);
    }
  }, [currentPage, menus]);

  const menuItems = buildPermissionMenus();

  const menuProps: MenuProps = {
    mode: 'horizontal',
    selectedKeys: currentMenu,
    items: menuItems as any, // 临时类型转换，避免类型冲突
    onSelect: handleMenuSelect,
    style: {
      lineHeight: '48px',
      border: 'none',
      backgroundColor: 'transparent'
    }
  };

  if (!menuItems.length) {
    return null;
  }

  return (
    <div className="header-menu">
      <Menu {...menuProps} />
    </div>
  );
};

export default HeaderMenu;
